import { useState, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectGroup,
    SelectItem,
} from "../../../components/ui/select";
import { Label } from "../../../components/ui/label";
import { Checkbox } from "../../../components/ui/checkbox";
import apiClient from "../../../utils/apiClient";
import apiClientChoice from "../../../utils/apiClientChoice";
import { Input } from "../../../components/ui/input";
import { Eye, EyeOff } from "lucide-react";
import toast from "react-hot-toast";
import server from "../../../utils/serverConfig";

const statusOptions = [
    { value: "active", label: "active" },
    { value: "inactive", label: "inactive" },
    { value: "deactivated", label: "deactivated" },
    { value: "dummy", label: "dummy" },
];

const accountStatusOptions = [
    { value: "ACTIVE", label: "ACTIVE" },
    { value: "INACTIVE", label: "INACTIVE" },
];

const labelOptions = [
    { value: "clearmind", label: "clearmind" },
    { value: "satyam", label: "satyam" },
];

export function DhanHQUserForm({ 
    handleAddDhanHQUserIndexMultiplier, 
    onClose, 
    proxyData, 
    isOnholdLegality, 
    isLegalitiesComplete, 
    isPolarisOrPolarisLite, 
    isWizard = false,
    onWizardSubmit,
    prefilledName = "",
    prefilledPhone = "",
    prefilledEmail = "",
    prefilledDob = "",
    prefilledClientId = "",
    prefilledTotpKey = "",
    prefilledPin = "",
    prefilledRedirectUrl = "",
    prefilledPassword = "",
    prefilledApiKey = "",
    prefilledApiSecret = "",
    prefilledSizing = 0,
    prefilledAccountStatus = "INACTIVE",
    prefilledLabel = "",
    prefilledProxyUser = "",
    prefilledProxyPassword = "",
    prefilledProxyHost = "",
    prefilledProxyPort = 0,
    prefilledProxyStaticIp = "",
    prefilledProxyToggle = false
}) {
    // ── Common states ────────────────────────────────────────────────────────
    const [clientId, setClientId] = useState(prefilledClientId || "");
    const [name, setName] = useState(prefilledName || "");
    const [phone, setPhone] = useState(prefilledPhone || "");
    const [totpKey, setTotpKey] = useState(prefilledTotpKey || "");
    const [pin, setPin] = useState(prefilledPin || "");
    const [redirectUrl, setRedirectUrl] = useState(prefilledRedirectUrl || "");
    const [status, setStatus] = useState("");
    const [password, setPassword] = useState(prefilledPassword || "");
    const [apiKey, setApiKey] = useState(prefilledApiKey || "");
    const [apiSecret, setApiSecret] = useState(prefilledApiSecret || "");
    const [label, setLabel] = useState(prefilledLabel || "");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ── Polaris-only states ──────────────────────────────────────────────────
    const [email, setEmail] = useState(prefilledEmail || "");
    const [dob, setDob] = useState(prefilledDob || "");
    const [sizing, setSizing] = useState(prefilledSizing || 0);
    const [accountStatus, setAccountStatus] = useState(prefilledAccountStatus || "INACTIVE");

    // ── Proxy states ─────────────────────────────────────────────────────────
    const [proxyUser, setProxyUser] = useState(prefilledProxyUser || "");
    const [proxyPassword, setProxyPassword] = useState(prefilledProxyPassword || "");
    const [proxyHost, setProxyHost] = useState(prefilledProxyHost || "");
    const [proxyPort, setProxyPort] = useState(prefilledProxyPort || 0);
    const [proxyToggle, setProxyToggle] = useState(prefilledProxyToggle || false);
    const [proxyStaticIp, setProxyStaticIp] = useState(prefilledProxyStaticIp || "");

    // ── Sync prefilled props asynchronously ───────────────────────────────────
    useEffect(() => {
        if (prefilledClientId) setClientId(prefilledClientId);
        if (prefilledName) setName(prefilledName);
        if (prefilledPhone) setPhone(prefilledPhone);
        if (prefilledTotpKey) setTotpKey(prefilledTotpKey);
        if (prefilledPin) setPin(prefilledPin);
        if (prefilledRedirectUrl) setRedirectUrl(prefilledRedirectUrl);
        if (prefilledPassword) setPassword(prefilledPassword);
        if (prefilledApiKey) setApiKey(prefilledApiKey);
        if (prefilledApiSecret) setApiSecret(prefilledApiSecret);
        if (prefilledLabel) setLabel(prefilledLabel);
        if (prefilledEmail) setEmail(prefilledEmail);
        if (prefilledDob) setDob(prefilledDob);
        if (prefilledSizing !== undefined && prefilledSizing !== null) setSizing(prefilledSizing);
        if (prefilledAccountStatus) setAccountStatus(prefilledAccountStatus);
        if (prefilledProxyUser) setProxyUser(prefilledProxyUser);
        if (prefilledProxyPassword) setProxyPassword(prefilledProxyPassword);
        if (prefilledProxyHost) setProxyHost(prefilledProxyHost);
        if (prefilledProxyPort !== undefined && prefilledProxyPort !== null) setProxyPort(prefilledProxyPort);
        if (prefilledProxyStaticIp) setProxyStaticIp(prefilledProxyStaticIp);
        if (prefilledProxyToggle !== undefined && prefilledProxyToggle !== null) setProxyToggle(prefilledProxyToggle);
    }, [
        prefilledClientId, prefilledName, prefilledPhone, prefilledTotpKey,
        prefilledPin, prefilledRedirectUrl, prefilledPassword, prefilledApiKey,
        prefilledApiSecret, prefilledLabel, prefilledEmail, prefilledDob,
        prefilledSizing, prefilledAccountStatus, prefilledProxyUser,
        prefilledProxyPassword, prefilledProxyHost, prefilledProxyPort,
        prefilledProxyStaticIp, prefilledProxyToggle
    ]);

    // ── Multiplier states (non-polaris) ───────────────────────────────────────
    const [multiplier, setMultiplier] = useState(0);
    const [bankniftyMultiplier, setBankniftyMultiplier] = useState(0);
    const [finniftyMultiplier, setFinniftyMultiplier] = useState(0);
    const [midcpniftyMultiplier, setMidcpniftyMultiplier] = useState(0);
    const [sensexMultiplier, setSensexMultiplier] = useState(0);

    // ── Visibility states ────────────────────────────────────────────────────
    const [showPin, setShowPin] = useState(false);
    const [showTotpKey, setShowTotpKey] = useState(false);
    const [showApiKey, setShowApiKey] = useState(false);
    const [showApiSecret, setShowApiSecret] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showProxyPassword, setShowProxyPassword] = useState(false);

    // ── Effects ───────────────────────────────────────────────────────────────
    useEffect(() => {
        if (isOnholdLegality && !isLegalitiesComplete) {
            setStatus("inactive");
            setAccountStatus("INACTIVE");
        }
    }, [isOnholdLegality, isLegalitiesComplete]);

    useEffect(() => {
        if (isPolarisOrPolarisLite) {
            setMultiplier(0);
            setBankniftyMultiplier(0);
            setFinniftyMultiplier(0);
            setMidcpniftyMultiplier(0);
            setSensexMultiplier(0);
        } else {
            setMultiplier(1);
        }
    }, [isPolarisOrPolarisLite]);

    useEffect(() => {
        if (proxyData) {
            setProxyUser(proxyData.proxy_user || "");
            setProxyPassword(proxyData.proxy_password || "");
            setProxyHost(proxyData.proxy_host || "");
            setProxyPort(proxyData.proxy_port || 0);
            setProxyStaticIp(proxyData.proxy_static_ip || "");
            setProxyToggle(true);
        }
    }, [proxyData]);

    // ── Polaris (Choice-server) submit ────────────────────────────────────────
    const handleAddPolarisChoiceDhanHQUser = () => {
        const cId = (clientId || "").trim();
        const nm = (name || "").trim();
        const pwd = (password || "").trim();
        const key = (apiKey || "").trim();
        const sec = (apiSecret || "").trim();
        const totp = (totpKey || "").trim();

        if (!cId || !nm || !pwd || !key || !sec || !totp) {
            toast.error("Missing required fields!", { duration: 5000 });
            return;
        }

        const payload = {
            name: nm,
            email: (email || "").trim(),
            password: pwd,
            phone: (phone || "").trim(),
            client_id: cId,
            active: accountStatus === "ACTIVE",
            broker: "DHANHQ",
            multiplier: "0",
            dob: (dob || "").trim(),
            api_key: key,
            api_secret_key: sec,
            totp_key: totp,
            proxy_user: (proxyUser || "").trim(),
            proxy_password: (proxyPassword || "").trim(),
            proxy_host_db: (proxyHost || "").trim(),
            proxy_port: parseInt(proxyPort) || 0,
            proxy_static_ip: (proxyStaticIp || "").trim(),
            proxy_toggle: !!proxyToggle,
            type: "",
            session_id: "",
            pin: pwd,
            app_secret: sec,
            proxy_host: (proxyHost || "").trim(),
            vendor_id: cId,
            sizing: parseInt(sizing) || 0,
            account_status: accountStatus,
        };

        // Wizard mode: save for approval
        if (isWizard && onWizardSubmit) {
            onWizardSubmit({
                client_id: cId,
                brokerData: { ...payload },
            });
            return;
        }

        console.log("Polaris DhanHQ payload:", payload);

        setIsSubmitting(true);
        apiClientChoice
            .request({
                method: "post",
                url: "https://choice.server.sypto.xyz/add/user",
                headers: { "Content-Type": "application/json" },
                data: JSON.stringify(payload),
            })
            .then((response) => {
                console.log("Choice server response:", response.data);
                toast.success("DhanHQ (Polaris) user added successfully!", { duration: 5000 });
                if (handleAddDhanHQUserIndexMultiplier) {
                    handleAddDhanHQUserIndexMultiplier(cId, nm, 0, 0, 0, 0, 0);
                }
                onClose(true);
            })
            .catch((error) => {
                console.error("Failed to add Polaris DhanHQ user:", error);
                toast.error("Failed to add user");
                onClose(false);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    // ── Standard (Godseye) submit ─────────────────────────────────────────────
    const handleAddDhanHQUser = () => {
        const cId = (clientId || "").trim();
        const nm = (name || "").trim();
        const ph = (phone || "").trim();
        const totp = (totpKey || "").trim();
        const p = (pin || "").trim();
        const rUrl = (redirectUrl || "").trim();
        const st = (status || "").trim();
        const pwd = (password || "").trim();
        const key = (apiKey || "").trim();
        const sec = (apiSecret || "").trim();
        const lbl = (label || "").trim();
        const pUser = (proxyUser || "").trim();
        const pPwd = (proxyPassword || "").trim();
        const pHost = (proxyHost || "").trim();
        const pStaticIp = (proxyStaticIp || "").trim();

        const data = {
            client_id: cId,
            name: nm,
            phone: ph,
            totp_key: totp,
            pin: p,
            redirect_url: rUrl,
            status: st,
            password: pwd,
            api_key: key,
            api_secret: sec,
            label: lbl,
            proxy_user: pUser,
            proxy_password: pPwd,
            proxy_host: pHost,
            proxy_port: parseInt(proxyPort) || 0,
            proxy_toggle: !!proxyToggle,
            proxy_static_ip: pStaticIp
        };

        if (!data.client_id || !data.name || !data.status || !data.api_key || !data.api_secret || !data.label) {
            console.log("Missing required fields:", data);
            toast.error("Missing required fields !!", { duration: 5000 });
            return;
        }

        // Wizard mode: save for approval
        if (isWizard && onWizardSubmit) {
            onWizardSubmit({
                client_id: data.client_id,
                brokerData: {
                    ...data,
                    multiplier: parseInt(multiplier) || 0,
                    banknifty_multiplier: parseInt(bankniftyMultiplier) || 0,
                    finnifty_multiplier: parseInt(finniftyMultiplier) || 0,
                    midcpnifty_multiplier: parseInt(midcpniftyMultiplier) || 0,
                    sensex_multiplier: parseInt(sensexMultiplier) || 0,
                },
            });
            return;
        }

        console.log("New Dhan HQ User Data", data);

        let config = {
            method: "post",
            url: `${server.godseye.baseUrl}add-dhan-hq-user`,
            headers: { "Content-Type": "application/json" },
            data: JSON.stringify(data),
        };

        setIsSubmitting(true);
        apiClient
            .request(config)
            .then((response) => {
                console.log("Server response:", response.data);
                if (response.data.status === 200 || response.data.message) {
                    toast.success("New Dhan HQ user added successfully!", { duration: 5000 });
                    if (handleAddDhanHQUserIndexMultiplier) {
                        handleAddDhanHQUserIndexMultiplier(
                            data.client_id,
                            data.name,
                            multiplier,
                            bankniftyMultiplier,
                            finniftyMultiplier,
                            midcpniftyMultiplier,
                            sensexMultiplier
                        );
                    }
                    onClose(true);
                } else {
                    onClose(false);
                }
            })
            .catch((error) => {
                console.error("Failed to add new Dhan HQ user", error);
                toast.error("Failed to add user");
                onClose(false);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    // ─────────────────────────────────────────────────────────────────────────
    // ── Polaris (Choice-server) form ─────────────────────────────────────────
    // ─────────────────────────────────────────────────────────────────────────
    if (isPolarisOrPolarisLite) {
        return (
            <div className={isWizard ? "font-inter space-y-4" : "font-inter space-y-4 max-h-[60vh] overflow-y-auto custom-scroll pr-4"}>
                <div className="grid grid-cols-4 gap-6 py-4">
                    {/* Client ID */}
                    <div className="grid grid-cols-1 items-center gap-4">
                        <Label className="text-white">Client Id <span className="text-red-500">*</span></Label>
                        <Input className="text-white" type="text" value={clientId} onChange={(e) => setClientId(e.target.value)} />
                    </div>

                    {/* Name */}
                    <div className="grid grid-cols-1 items-center gap-4">
                        <Label className="text-white">Name <span className="text-red-500">*</span></Label>
                        <Input className="text-white" type="text" value={name} onChange={(e) => setName(e.target.value)} />
                    </div>

                    {/* Email */}
                    <div className="grid grid-cols-1 items-center gap-4">
                        <Label className="text-white">Email</Label>
                        <Input className="text-white" type="text" value={email} onChange={(e) => setEmail(e.target.value)} />
                    </div>

                    {/* Phone */}
                    <div className="grid grid-cols-1 items-center gap-4">
                        <Label className="text-white">Phone</Label>
                        <Input className="text-white" type="text" value={phone} onChange={(e) => setPhone(e.target.value)} />
                    </div>

                    {/* Password */}
                    <div className="grid grid-cols-1 items-center gap-4">
                        <Label className="text-white">Password <span className="text-red-500">*</span></Label>
                        <div className="relative">
                            <Input className="text-white pr-10" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} />
                            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200">
                                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </div>

                    {/* API Key */}
                    <div className="grid grid-cols-1 items-center gap-4">
                        <Label className="text-white">API Key <span className="text-red-500">*</span></Label>
                        <div className="relative">
                            <Input className="text-white pr-10" type={showApiKey ? "text" : "password"} value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
                            <button type="button" onClick={() => setShowApiKey(!showApiKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200">
                                {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </div>

                    {/* API Secret Key */}
                    <div className="grid grid-cols-1 items-center gap-4">
                        <Label className="text-white">API Secret Key <span className="text-red-500">*</span></Label>
                        <div className="relative">
                            <Input className="text-white pr-10" type={showApiSecret ? "text" : "password"} value={apiSecret} onChange={(e) => setApiSecret(e.target.value)} />
                            <button type="button" onClick={() => setShowApiSecret(!showApiSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200">
                                {showApiSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </div>

                    {/* TOTP Key */}
                    <div className="grid grid-cols-1 items-center gap-4">
                        <Label className="text-white">TOTP Key <span className="text-red-500">*</span></Label>
                        <div className="relative">
                            <Input className="text-white pr-10" type={showTotpKey ? "text" : "password"} value={totpKey} onChange={(e) => setTotpKey(e.target.value)} />
                            <button type="button" onClick={() => setShowTotpKey(!showTotpKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200">
                                {showTotpKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </div>

                    {/* DOB */}
                    <div className="grid grid-cols-1 items-center gap-4">
                        <Label className="text-white">Date of Birth</Label>
                        <Input className="text-white" type="text" placeholder="YYYY-MM-DD" value={dob} onChange={(e) => setDob(e.target.value)} />
                    </div>

                    {/* Sizing */}
                    <div className="grid grid-cols-1 items-center gap-4">
                        <Label className="text-white">Sizing <span className="text-red-500">*</span></Label>
                        <Input className="text-white" type="number" value={sizing} onChange={(e) => setSizing(e.target.value)} />
                    </div>

                    {/* Account Status */}
                    <div className="grid grid-cols-1 items-center gap-4">
                        <Label className="text-white">Account Status <span className="text-red-500">*</span></Label>
                        <Select value={accountStatus} onValueChange={(val) => setAccountStatus(val)} disabled={isOnholdLegality && !isLegalitiesComplete}>
                            <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
                                <SelectValue placeholder="Select Status" />
                            </SelectTrigger>
                            <SelectContent className="border-zinc-700">
                                <SelectGroup>
                                    {accountStatusOptions.map((s) => (
                                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                    ))}
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Proxy User */}
                    <div className="grid grid-cols-1 items-center gap-4">
                        <Label className="text-white">Proxy User</Label>
                        <Input className="text-white" type="text" value={proxyUser} onChange={(e) => setProxyUser(e.target.value)} />
                    </div>

                    {/* Proxy Password */}
                    <div className="grid grid-cols-1 items-center gap-4">
                        <Label className="text-white">Proxy Password</Label>
                        <div className="relative">
                            <Input className="text-white pr-10" type={showProxyPassword ? "text" : "password"} value={proxyPassword} onChange={(e) => setProxyPassword(e.target.value)} />
                            <button type="button" onClick={() => setShowProxyPassword(!showProxyPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200">
                                {showProxyPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </div>

                    {/* Proxy Host */}
                    <div className="grid grid-cols-1 items-center gap-4">
                        <Label className="text-white">Proxy Host</Label>
                        <Input className="text-white" type="text" value={proxyHost} onChange={(e) => setProxyHost(e.target.value)} />
                    </div>

                    {/* Proxy Port */}
                    <div className="grid grid-cols-1 items-center gap-4">
                        <Label className="text-white">Proxy Port</Label>
                        <Input className="text-white" type="number" value={proxyPort} onChange={(e) => setProxyPort(e.target.value)} />
                    </div>

                    {/* Proxy Static IP */}
                    <div className="grid grid-cols-1 items-center gap-4">
                        <Label className="text-white">Proxy Static IP</Label>
                        <Input className="text-white" type="text" value={proxyStaticIp} onChange={(e) => setProxyStaticIp(e.target.value)} />
                    </div>

                    {/* Proxy Toggle */}
                    <div className="flex items-center space-x-2 h-full pt-4">
                        <Checkbox id="proxy-toggle-polaris-dhq" checked={proxyToggle} onCheckedChange={(checked) => setProxyToggle(checked)} />
                        <Label htmlFor="proxy-toggle-polaris-dhq" className="text-white cursor-pointer">Proxy Toggle</Label>
                    </div>
                </div>

                <div className="mt-6 flex justify-end pb-4">
                    <Button
                        disabled={isSubmitting || !clientId || !name || !password || !apiKey || !apiSecret || !totpKey}
                        type="submit"
                        className="text-[16px] font-normal bg-zinc-800 text-white hover:bg-zinc-700 disabled:opacity-50"
                        onClick={handleAddPolarisChoiceDhanHQUser}
                    >
                        {isSubmitting ? "Adding..." : isWizard ? "Submit for Approval" : "Add DhanHQ (Polaris) User"}
                    </Button>
                </div>
            </div>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // ── Standard (Godseye) form ───────────────────────────────────────────────
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className={isWizard ? "font-inter space-y-4" : "font-inter space-y-4 max-h-[60vh] overflow-y-auto custom-scroll pr-4"}>
            <div className="grid grid-cols-4 gap-6 py-4">
                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        Client Id <span className="text-red-500">*</span>
                    </Label>
                    <Input
                        className="text-white"
                        type="text"
                        value={clientId}
                        onChange={(e) => setClientId(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                        className="text-white"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Phone</Label>
                    <Input
                        className="text-white"
                        type="text"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        Status <span className="text-red-500">*</span>
                    </Label>
                    <Select value={status} onValueChange={(val) => setStatus(val)} disabled={isOnholdLegality && !isLegalitiesComplete}>
                        <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
                            <SelectValue placeholder="Select Status" />
                        </SelectTrigger>
                        <SelectContent className="border-zinc-700">
                            <SelectGroup>
                                {statusOptions.map((s) => (
                                    <SelectItem key={s.value} value={s.value}>
                                        {s.label}
                                    </SelectItem>
                                ))}
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Label <span className="text-red-500">*</span></Label>
                    <Input
                        className="text-white"
                        type="text"
                        list="dhanhq-label-options"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder="Select or type label"
                    />
                    <datalist id="dhanhq-label-options">
                        {labelOptions.map((l) => (
                            <option key={l.value} value={l.value} />
                        ))}
                    </datalist>
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        API Key <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                        <Input
                            className="text-white pr-10"
                            type={showApiKey ? "text" : "password"}
                            value={apiKey}
                            onChange={(e) => setApiKey(e.target.value)}
                        />
                        <button
                            type="button"
                            onClick={() => setShowApiKey(!showApiKey)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                        >
                            {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        API Secret <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                        <Input
                            className="text-white pr-10"
                            type={showApiSecret ? "text" : "password"}
                            value={apiSecret}
                            onChange={(e) => setApiSecret(e.target.value)}
                        />
                        <button
                            type="button"
                            onClick={() => setShowApiSecret(!showApiSecret)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                        >
                            {showApiSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">TOTP Key</Label>
                    <div className="relative">
                        <Input
                            className="text-white pr-10"
                            type={showTotpKey ? "text" : "password"}
                            value={totpKey}
                            onChange={(e) => setTotpKey(e.target.value)}
                        />
                        <button
                            type="button"
                            onClick={() => setShowTotpKey(!showTotpKey)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                        >
                            {showTotpKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">PIN</Label>
                    <div className="relative">
                        <Input
                            className="text-white pr-10"
                            type={showPin ? "text" : "password"}
                            value={pin}
                            onChange={(e) => setPin(e.target.value)}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPin(!showPin)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                        >
                            {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Redirect URL</Label>
                    <Input
                        className="text-white"
                        type="text"
                        value={redirectUrl}
                        onChange={(e) => setRedirectUrl(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Password</Label>
                    <div className="relative">
                        <Input
                            className="text-white pr-10"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                        >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Multiplier Number <span className="text-red-500">*</span></Label>
                    <Input
                        className="text-white"
                        type="number"
                        value={multiplier}
                        onChange={(e) => setMultiplier(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Bank Nifty Multiplier <span className="text-red-500">*</span></Label>
                    <Input
                        className="text-white"
                        type="number"
                        value={bankniftyMultiplier}
                        onChange={(e) => setBankniftyMultiplier(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Fin Nifty Multiplier <span className="text-red-500">*</span></Label>
                    <Input
                        className="text-white"
                        type="number"
                        value={finniftyMultiplier}
                        onChange={(e) => setFinniftyMultiplier(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Midcap Nifty Multiplier <span className="text-red-500">*</span></Label>
                    <Input
                        className="text-white"
                        type="number"
                        value={midcpniftyMultiplier}
                        onChange={(e) => setMidcpniftyMultiplier(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Sensex Multiplier <span className="text-red-500">*</span></Label>
                    <Input
                        className="text-white"
                        type="number"
                        value={sensexMultiplier}
                        onChange={(e) => setSensexMultiplier(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Proxy User</Label>
                    <Input
                        className="text-white"
                        type="text"
                        value={proxyUser}
                        onChange={(e) => setProxyUser(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Proxy Password</Label>
                    <div className="relative">
                        <Input
                            className="text-white pr-10"
                            type={showProxyPassword ? "text" : "password"}
                            value={proxyPassword}
                            onChange={(e) => setProxyPassword(e.target.value)}
                        />
                        <button
                            type="button"
                            onClick={() => setShowProxyPassword(!showProxyPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                        >
                            {showProxyPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Proxy Host</Label>
                    <Input
                        className="text-white"
                        type="text"
                        value={proxyHost}
                        onChange={(e) => setProxyHost(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Proxy Port</Label>
                    <Input
                        className="text-white"
                        type="number"
                        value={proxyPort}
                        onChange={(e) => setProxyPort(e.target.value)}
                    />
                </div>
                
                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Proxy Static IP</Label>
                    <Input
                        className="text-white"
                        type="text"
                        value={proxyStaticIp}
                        onChange={(e) => setProxyStaticIp(e.target.value)}
                    />
                </div>

                <div className="flex items-center space-x-2 h-full pt-4">
                    <Checkbox
                        id="proxy-toggle"
                        checked={proxyToggle}
                        onCheckedChange={(checked) => setProxyToggle(checked)}
                    />
                    <Label htmlFor="proxy-toggle" className="text-white cursor-pointer">
                        Proxy Toggle
                    </Label>
                </div>
            </div>

            <div className="mt-6 flex justify-end pb-4">
                <Button
                    disabled={
                        isSubmitting ||
                        !clientId ||
                        !name ||
                        !status ||
                        !apiKey ||
                        !apiSecret ||
                        !label
                    }
                    type="submit"
                    className="text-[16px] font-normal bg-zinc-800 text-white hover:bg-zinc-700 disabled:opacity-50"
                    onClick={handleAddDhanHQUser}
                >
                    {isSubmitting ? "Adding..." : isWizard ? "Submit for Approval" : "Add Dhan HQ User"}
                </Button>
            </div>
        </div>
    );
}
